import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  input,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  SimpleChanges,
  TemplateRef,
  WritableSignal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ControlsOf, FormControl, FormGroup } from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ModalRef, ModalService } from '@vinnova/ui';
import { IsPersonnummer } from '@vinnova/validation';
import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  forkJoin,
  map,
  merge,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  take,
} from 'rxjs';
import { Begaran } from '../../../core/models/begaran.model';
import { FormQuestionStandard } from '../../../core/models/form-group-mapping.model';
import { ReadonlyState } from '../../../core/models/form.model';
import {
  QuestionStructureWorksite,
  Worksite,
  WorksiteAnswer,
} from '../../../core/models/question/question-worksite.model';
import { Question } from '../../../core/models/question/question.model';
import { StructuredDataKey } from '../../../core/models/question/structured-data-key.model';
import { RendererFormsQuery } from '../../../core/queries/renderer-forms.query';
import { RendererFormsRepository } from '../../../core/repositories/renderer-forms.repository';
import { BaseBegaranService } from '../../../core/services/base-begaran.service';
import { BaseWorksiteService } from '../../../core/services/base-worksite.service';
import { FinancierService } from '../../../core/services/financier.service';
import { FormValidationService } from '../../../core/services/form-validation.service';
import { QuestionNumberService } from '../../../core/services/question-number.service';
import { QuestionValueChangeService } from '../../../core/services/question-value-change.service';
import { sortAlphabetically } from '../../../shared/utils/sorting.utils';
import { IProjektBudget } from '../budget/budget.interface';

@UntilDestroy()
@Component({
  selector: 'lib-question-worksite',
  templateUrl: './question-worksite.component.html',
  styleUrls: ['./question-worksite.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class QuestionWorksiteComponent implements OnInit, OnChanges, OnDestroy {
  oregModal: ModalRef;
  removeModal: ModalRef;
  toggleModal: ModalRef;
  personnummerVarningModal: ModalRef;
  questionNumber$: Observable<unknown>;
  readOnly$: Observable<ReadonlyState>;
  readOnly: Signal<ReadonlyState>;
  activeLang$: Observable<string>;
  projektpartsRequired: boolean;
  worksites: Worksite[] = [];
  coordinatorWorksite: Worksite;
  participantsWorksites: Worksite[] = [];

  allowedToAddCoordinatingParticipant = new BehaviorSubject<boolean>(true);
  allowedToAddCoordinatingParticipant$ =
    this.allowedToAddCoordinatingParticipant.asObservable();
  coordinatorCloseModal = new Subject<void>();
  participantCloseModal = new Subject<void>();
  removedCoordinator = new Subject<void>();
  removedParticipant = new Subject<void>();
  errorMessageCoordinator$: Observable<string>;
  errorMessageParticipant$: Observable<string>;
  helpTextParticipant$: Observable<string>;
  valueChangeSubscription: Subscription;
  patchValueSubscription: Subscription;
  begaranId: string;
  begaran = signal<Begaran>(undefined);
  showChanges: Signal<boolean>;
  worksiteInactiveToggleValue: WritableSignal<boolean>[] = [];
  isMobile = signal<boolean>(false);

  private _question: Question;
  @Input() set question(value: Question) {
    this._question = value;
    this.setHelpText();
  }
  get question() {
    return this._question;
  }

  @Input() labelPrefix: string;
  @Input() repeaterGroupId: string;
  isChangeRequest = input<boolean>(false);

  _questionForm: FormGroup<ControlsOf<FormQuestionStandard>>;
  @Input()
  set questionForm(form: FormGroup<ControlsOf<FormQuestionStandard>>) {
    this._questionForm = form;
    this.value?.touch$.pipe(take(1)).subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  get questionForm() {
    return this._questionForm;
  }

  get structure() {
    return this.question.structure as QuestionStructureWorksite;
  }

  get value(): FormControl<string> {
    return this.questionForm?.controls.value;
  }

  get allowedToAddMore() {
    if (!this.structure.maxNumberOfParticipants) {
      return true;
    }

    return (
      this.participantsWorksites.length < this.structure.maxNumberOfParticipants
    );
  }

  get hideParticipants() {
    if (
      this.isSharedView &&
      this.readOnly() !== ReadonlyState.Editable &&
      this.participantsWorksites.length === 0
    ) {
      return true;
    } else if (this.structure.hideParticipants != undefined) {
      return this.structure.hideParticipants;
    }

    return this.structure.maxNumberOfParticipants === 0;
  }

  get hideFinanciers() {
    return this.structure.hideFinanciers;
  }

  get worksiteIds() {
    return this.worksites.map((w) => w.id);
  }

  get worksiteProviderIds() {
    return this.worksites.map((w) => w.providerId);
  }

  get useToggle() {
    return (
      (this.isSharedView && this.worksiteService.hasBeslut) ||
      this.isChangeRequest()
    );
  }

  coordinatorId: string;

  coordinator = false;

  worksitePendingRemoval: Worksite;

  isSharedView: boolean;

  constructor(
    private cdr: ChangeDetectorRef,
    private questionValueChangeService: QuestionValueChangeService,
    private modalService: ModalService,
    private translocoService: TranslocoService,
    private worksiteService: BaseWorksiteService,
    private begaranService: BaseBegaranService,
    private questionNumberService: QuestionNumberService,
    private formsQuery: RendererFormsQuery,
    private financierService: FinancierService,
    private formValidationService: FormValidationService,
    private rendererFormRepository: RendererFormsRepository,
  ) {
    this.showChanges = toSignal(this.rendererFormRepository.showChanges$);
    this.readOnly = toSignal(this.formsQuery.selectReadOnly());
  }

  ngOnInit() {
    this.isSharedView = this.rendererFormRepository.getIsSharedView();

    this.readOnly$ = this.formsQuery
      .selectReadOnly()
      .pipe(distinctUntilChanged());
    this.activeLang$ = this.translocoService.langChanges$;

    this.questionValueChangeService.patchInitialValue(
      this.question.id,
      this.repeaterGroupId,
      this.value,
      this.question,
    );
    this.isMobile.set(
      /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
        navigator.userAgent,
      ),
    );

    if (this.isChangeRequest()) {
      this.begaranService.begaran$
        .pipe(untilDestroyed(this))
        .subscribe((begaran) => {
          this.begaran.set(begaran);
          this.worksiteService.effektaWorksites =
            this.begaran()?.budgetMedAndring?.worksites;
        });
    }

    this.setWorksites();
    if (!this.value.value) {
      this.updateValue(true);
    }

    this.setHelpText();

    this.questionNumber$ = this.questionNumberService.getQuestionNumberForLabel(
      this.question.id,
    );

    this.setErrorMessage();

    this.questionValueChangeService.sendInitialKeyValue(
      this.question,
      this.value,
      {
        questionId: this.question.id,
        repeaterGroupId: this.repeaterGroupId,
      },
      this.questionForm.valid,
    );

    this.rendererFormRepository.setShowChangesAnswers(
      'worksite',
      this.checkForChanges(),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['repeaterGroupId']) {
      if (this.valueChangeSubscription) {
        this.valueChangeSubscription.unsubscribe();
      }
      if (this.patchValueSubscription) {
        this.valueChangeSubscription.unsubscribe();
      }

      this.valueChangeSubscription = this.questionValueChangeService
        .valueChangeListener(
          this.value,
          {
            questionId: this.question.id,
            repeaterGroupId: this.repeaterGroupId,
          },
          this.question,
        )
        .pipe(untilDestroyed(this))
        .subscribe();

      this.patchValueSubscription = this.questionValueChangeService
        .patchValueListener(this.question, this.repeaterGroupId, this.value)
        .pipe(untilDestroyed(this))
        .subscribe(() => this.setWorksites());
    }
  }

  ngOnDestroy() {
    this.financierService.resetFinanciers();
  }

  setErrorMessage() {
    this.errorMessageCoordinator$ = this.readOnly$.pipe(
      switchMap((readonly) => {
        switch (readonly) {
          case ReadonlyState.Editable: {
            return merge(
              this.removedCoordinator,
              this.coordinatorCloseModal,
              this.formValidationService.showErrors$.pipe(filter((err) => err)),
            ).pipe(switchMap(() => this.coordinatorErrorMessage()));
          }
          case ReadonlyState.Locked: {
            return this.coordinatorErrorMessage();
          }
          case ReadonlyState.Reader: {
            return of(undefined);
          }
        }
      }),
    );
    this.errorMessageParticipant$ = this.readOnly$.pipe(
      switchMap((readonly) => {
        switch (readonly) {
          case ReadonlyState.Editable: {
            return merge(
              this.removedParticipant,
              this.participantCloseModal,
              this.formValidationService.showErrors$.pipe(filter((err) => err)),
            ).pipe(switchMap(() => this.participantErrorMessage()));
          }
          case ReadonlyState.Locked: {
            return this.participantErrorMessage();
          }
          case ReadonlyState.Reader: {
            return of(undefined);
          }
        }
      }),
    );
  }

  coordinatorErrorMessage() {
    const existingWorksites = JSON.parse(
      // eslint-disable-next-line unicorn/no-null
      this.value.value === undefined ? null : this.value.value,
    ) as [{ worksiteId: string; financiers: string[]; isCoordinator: boolean }];
    const hasCoordinator = existingWorksites?.some((w) => w.isCoordinator);
    return hasCoordinator
      ? of(undefined)
      : this.translocoService.selectTranslate('worksite_coordinator_mandatory');
  }

  setHelpText() {
    if (this.isChangeRequest()) {
      this.helpTextParticipant$ = of(undefined);
      return;
    }
    if (
      this.structure.minNumberOfParticipants &&
      !this.structure.maxNumberOfParticipants &&
      this.participantsWorksites.length < this.structure.minNumberOfParticipants
    ) {
      this.helpTextParticipant$ = this.minParticipantsText();
      return;
    } else if (
      this.structure.minNumberOfParticipants &&
      this.structure.maxNumberOfParticipants &&
      this.participantsWorksites.length !==
        this.structure.maxNumberOfParticipants &&
      !this.isChangeRequest()
    ) {
      this.helpTextParticipant$ = this.minAndMaxParticipantsText();
      return;
    } else if (
      !this.structure.minNumberOfParticipants &&
      this.structure.maxNumberOfParticipants &&
      this.participantsWorksites.length !==
        this.structure.maxNumberOfParticipants
    ) {
      this.helpTextParticipant$ = this.maxParticipantsText();
      return;
    }
    this.helpTextParticipant$ = of(undefined);
  }

  participantErrorMessage() {
    if (this.isChangeRequest()) {
      return of(undefined);
    }
    if (
      this.structure.minNumberOfParticipants &&
      !this.structure.maxNumberOfParticipants &&
      this.participantsWorksites.length < this.structure.minNumberOfParticipants
    ) {
      return this.minParticipantsText();
    } else if (
      this.structure.minNumberOfParticipants &&
      this.structure.maxNumberOfParticipants &&
      this.participantsWorksites.length <
        this.structure.minNumberOfParticipants &&
      !this.isChangeRequest()
    ) {
      return this.minAndMaxParticipantsText();
    }
    return of(undefined);
  }

  private minAndMaxParticipantsText(): Observable<string> {
    return this.translocoService.selectTranslate(
      'worksite_participants_min_max',
      {
        minParticipants: this.structure.minNumberOfParticipants + 1,
        maxParticipants: this.structure.maxNumberOfParticipants + 1,
      },
    );
  }

  private minParticipantsText(): Observable<string> {
    return this.translocoService.selectTranslate('worksite_participants_min', {
      minParticipants: this.structure.minNumberOfParticipants + 1,
    });
  }

  private maxParticipantsText(): Observable<string> {
    return this.translocoService.selectTranslate('worksite_participants_max', {
      maxParticipants: this.structure.maxNumberOfParticipants + 1,
    });
  }

  isForeign(id: string) {
    const worksiteIndex = this.worksites.findIndex((w) => w.id === id);
    if (worksiteIndex !== -1) {
      const worksite = this.worksites[worksiteIndex];
      return worksite.company?.country?.toLowerCase() !== 'se';
    }
  }

  onOpenRemoveWorksiteModal(content: TemplateRef<unknown>, worksite: Worksite) {
    this.removeModal = this.modalService.show(content, {
      backdrop: 'static',
      class: 'modal-m modal-fullscreen-md-down',
    });
    this.worksitePendingRemoval = worksite;
  }

  onRemoveWorksite() {
    this.removeWorksite(this.worksitePendingRemoval);
    this.updateValue();
    this.worksitePendingRemoval = undefined;
    this.removeModal.hide();
    this.formValidationService.triggerSectionInvalidCheck(this.question.id);
    this.setHelpText();
  }

  onCloseRemoveModal() {
    this.worksitePendingRemoval = undefined;
    this.removeModal.hide();
  }

  onClosePersonnummerVarningModal() {
    this.personnummerVarningModal.hide();
  }

  onOpenOregModal(content: TemplateRef<unknown>, coordinator?) {
    this.coordinator = coordinator;
    this.oregModal = this.modalService.show(content, {
      backdrop: 'static',
      class: 'modal-xl modal-fullscreen-md-down',
    });
    this.modalService
      .onHide(this.oregModal)
      .pipe(take(1))
      .subscribe(() => {
        this.value.markAllAsTouched();
        this.value.markAsDirty();
        this.formValidationService.triggerSectionInvalidCheck(this.question.id);
        if (coordinator) {
          this.coordinatorCloseModal.next();
        } else {
          this.participantCloseModal.next();
        }
        this.setHelpText();
      });
  }

  onCloseOregModal() {
    this.oregModal.hide();
  }

  onSelectedWorksite(content: TemplateRef<unknown>, worksite: Worksite) {
    worksite.isNew = true;
    this.worksiteService.addWorksite(worksite);
    this.addWorksite(worksite);
    this.updateValue();
    this.oregModal.hide();
    if (IsPersonnummer(worksite.company.nationalRegistrationNumber)) {
      this.personnummerVarningModal = this.modalService.show(content, {
        backdrop: 'static',
      });
    }
  }

  private setWorksites() {
    const worksiteAnswers: WorksiteAnswer[] = this.value.value
      ? JSON.parse(this.value.value)
      : this.isChangeRequest() && this.begaran()
        ? this.begaran().budgetMedAndring.worksites.map((worksite) => ({
            worksiteId: worksite.id,
            isCoordinator: worksite.isCoordinator ?? false,
            financiers: worksite.financiers.map((f) => f.id),
            isInactive: worksite.isInactive,
            isInactiveChanged: worksite.isInactiveChanged,
            isNew: worksite.isNew,
            newFinanciers: worksite.newFinanciers ?? [],
          }))
        : [];

    const observableWorksites: Observable<Worksite>[] = worksiteAnswers.map(
      (answer) => this.worksiteService.getWorksite(answer.worksiteId),
    );
    if (observableWorksites.length === 0) {
      this.worksites = [];
      this.coordinatorWorksite = undefined;
      this.allowedToAddCoordinatingParticipant.next(true);
      this.participantsWorksites = [];
    }

    forkJoin(observableWorksites)
      .pipe(take(1))
      .subscribe((worksites) => {
        const worksitesValue: Worksite[] = [];
        const participants: Worksite[] = [];
        let coordinator: Worksite;

        for (let worksite of worksites) {
          const worksiteAnswer = worksiteAnswers.find(
            (i) => i.worksiteId === worksite.id,
          );
          worksite = {
            ...worksite,
            isCoordinator: worksiteAnswer.isCoordinator,
            isInactive: worksiteAnswer.isInactive,
            isInactiveChanged: worksiteAnswer.isInactiveChanged,
            isNew: worksiteAnswer.isNew,
          };
          this.financierService.addWorksite(
            worksite.id,
            worksiteAnswer.financiers ?? [],
          );
          this.financierService.addNewFinanciers(
            worksite.id,
            worksiteAnswer.newFinanciers ?? [],
          );

          worksitesValue.push(worksite);
          if (worksiteAnswer.isCoordinator) {
            coordinator = worksite;
          } else {
            participants.push(worksite);
            this.worksiteInactiveToggleValue[worksite.id] = signal(
              !worksite.isInactive,
            );
          }
        }

        this.worksites = sortAlphabetically(worksitesValue);
        this.coordinatorWorksite = coordinator;
        this.allowedToAddCoordinatingParticipant.next(
          coordinator ? false : true,
        );
        this.participantsWorksites = sortAlphabetically(participants);

        this.worksiteService.setWorksites(worksitesValue);
      });
  }

  private addWorksite(worksite: Worksite) {
    const existingWorksites = JSON.parse(
      // eslint-disable-next-line unicorn/no-null
      this.value.value === undefined ? null : this.value.value,
    ) as WorksiteAnswer[];

    if (existingWorksites?.some((w) => w.worksiteId === worksite.id)) return;

    worksite.isCoordinator = this.coordinator;
    this.coordinatorId = this.coordinator ? worksite.id : undefined;
    if (this.coordinator) {
      this.allowedToAddCoordinatingParticipant.next(false);

      const existingCoordinator = existingWorksites?.find(
        (w) => w.isCoordinator,
      );
      if (existingCoordinator) {
        this.financierService.removeWorksite(existingCoordinator.worksiteId);
        this.worksiteService.removeWorksite(existingCoordinator.worksiteId);
        this.worksites = this.worksites.filter(
          (w) => existingCoordinator.worksiteId !== w.id,
        );
      }
      this.coordinatorWorksite = worksite;
    } else {
      this.participantsWorksites.push(worksite);
      this.participantsWorksites.sort((a, b) =>
        a.company.name.toLowerCase() > b.company.name.toLowerCase() ? 1 : -1,
      );
    }
    this.worksites.push(worksite);
    this.financierService.addWorksite(worksite.id, []);
    this.worksiteService.addWorksite(worksite);
    this.coordinator = false;
  }

  private removeWorksite(worksite: Worksite) {
    this.worksites = this.worksites.filter((w) => w.id !== worksite.id);
    this.participantsWorksites = this.participantsWorksites.filter(
      (w) => w.id !== worksite.id,
    );
    this.financierService.removeWorksite(worksite.id);
    this.worksiteService.removeWorksite(worksite.id);
    this.removedParticipant.next();
  }

  onFinancierAdded(financierId: string, worksiteId: string) {
    this.financierService.addFinancier(worksiteId, financierId);
    this.financierService.addNewFinancier(worksiteId, financierId);

    this.updateValue();
  }

  onFinancierRemoved(financierId: string, worksiteId: string) {
    this.financierService.removeFinancier(worksiteId, financierId);
    this.updateValue();
  }

  private updateValue(intialChange = false): void {
    const worksiteAnswers: WorksiteAnswer[] = this.worksites.map(
      (worksite) => ({
        worksiteId: worksite.id,
        isCoordinator: worksite.isCoordinator,
        financiers: this.financierService.getFinanciers(worksite.id),
        isInactive: worksite.isInactive,
        isInactiveChanged: worksite.isInactiveChanged,
        isNew: worksite.isNew,
        newFinanciers: [
          ...this.financierService.getNewFinanciers(worksite.id).values(),
        ],
        hasChanges:
          worksite.isInactiveChanged ||
          worksite.isNew ||
          this.financierService.getNewFinanciers(worksite.id).size > 0,
      }),
    );
    this.value.patchValue(JSON.stringify(worksiteAnswers));

    if (!intialChange) {
      this.value.markAsDirty();
    }
    this.rendererFormRepository.setShowChangesAnswers(
      'worksite',
      this.checkForChanges(),
    );
  }

  checkForChanges(): boolean {
    const effektaWorksites = [];
    const parsedValue = this.value.value
      ? JSON.parse(this.value.value)
      : undefined;
    if (this.begaran() || this.isSharedView) {
      for (const worksite of this.worksiteService.effektaWorksites) {
        const financiers: string[] = [];
        if (worksite.financiers) {
          for (const financier of worksite.financiers) {
            financiers.push(financier.id);
          }
        }
        effektaWorksites.push({
          worksiteId: worksite.id,
          financiers: financiers,
          isInactive: worksite.isInactive,
          isInactiveChanged: worksite.isInactiveChanged,
        });
      }
    }
    if (effektaWorksites.length !== parsedValue?.length) return true;
    for (const valueWorksite of parsedValue) {
      if (
        valueWorksite.isNew ||
        valueWorksite.isInactiveChanged ||
        valueWorksite.newFinanciers?.length > 0
      ) {
        return true;
      }
    }
    return false;
  }

  trackByFn(index) {
    return index;
  }

  toggle(w, confirmToggleModal: TemplateRef<unknown>) {
    if (w.isInactive) {
      this.toggleWorksite(w);
      return;
    }

    const hasFutureBudget$ = this.hasFutureBudget(w);
    hasFutureBudget$.pipe(take(1)).subscribe((val) => {
      if (val) {
        this.worksiteInactiveToggleValue[w.id].set(true);
        this.toggleModal = this.modalService.show(confirmToggleModal, {
          backdrop: 'static',
        });
      } else {
        this.toggleWorksite(w);
      }
    });
  }

  onCloseToggleModal() {
    this.toggleModal.hide();
  }

  private toggleWorksite(worksite: Worksite) {
    worksite.isInactive = !worksite.isInactive;
    const originalWorksite = this.worksiteService.effektaWorksites?.find(
      (w) => w.id === worksite.id,
    );
    worksite.isInactiveChanged = originalWorksite
      ? worksite.isInactive !== originalWorksite.isInactive
      : worksite.isInactive;
    this.updateValue();
  }

  private hasFutureBudget(worksite: Worksite) {
    const answer$ = this.questionValueChangeService.getOrCreateKeyValue(
      StructuredDataKey.Budget,
    );
    return answer$.pipe(
      map((answer) => {
        const value = answer.answer?.value;
        if (!value) {
          return false;
        }
        const parsedValue: IProjektBudget = JSON.parse(value);
        const parsedWorksiteValue = parsedValue.projektparter.find(
          (pp) => pp.id === worksite.id,
        );
        const yearNow = new Date().getFullYear();
        const anyFinansieringFuture = parsedWorksiteValue.bidragPerAr
          .filter((bidrag) => bidrag.ar > yearNow)
          .some((bidrag) => bidrag.summa > 0);
        const anyKostnaderFuture = parsedWorksiteValue.kostnader.some(
          (kostnad) =>
            kostnad.kostnaderPerAr
              .filter((k) => k.ar > yearNow)
              .some((k) => k.summa > 0),
        );
        const anyFinansiarFutureOrMissing =
          parsedWorksiteValue.finansiarer.some(
            (finansiar) =>
              finansiar.finansieringPerAr.every((f) => f.summa === 0) ||
              finansiar.finansieringPerAr
                .filter((f) => f.ar > yearNow)
                .some((f) => f.summa > 0),
          );
        return (
          anyFinansieringFuture ||
          anyKostnaderFuture ||
          anyFinansiarFutureOrMissing
        );
      }),
    );
  }
}
